import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { UPDATE_EOD_SESSION } from '../../../graphql/production-mutations'
import toast from 'react-hot-toast'

interface EodSession {
  id: number
  general_notes: string | null
  notes_for_tomorrow: string | null
}

interface NotesSectionProps {
  session: EodSession
  canLog: boolean
  onUpdate: (updated: Partial<EodSession>) => void
}

const NotesSection = ({ session, canLog, onUpdate }: NotesSectionProps) => {
  const [generalNotes, setGeneralNotes] = useState(session.general_notes ?? '')
  const [notesForTomorrow, setNotesForTomorrow] = useState(session.notes_for_tomorrow ?? '')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setGeneralNotes(session.general_notes ?? '')
    setNotesForTomorrow(session.notes_for_tomorrow ?? '')
    setDirty(false)
  }, [session.id])

  const [updateSession, { loading }] = useMutation(UPDATE_EOD_SESSION, {
    onCompleted: (data) => {
      onUpdate(data.updateEodSession)
      setDirty(false)
      toast.success('Notes saved')
    },
    onError: (e) => toast.error(e.message),
  })

  const handleSave = () => {
    updateSession({
      variables: {
        id: session.id,
        data: {
          general_notes: generalNotes.trim() || null,
          notes_for_tomorrow: notesForTomorrow.trim() || null,
        },
      },
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">General notes</label>
        <textarea
          value={generalNotes}
          onChange={(e) => { setGeneralNotes(e.target.value); setDirty(true) }}
          disabled={!canLog}
          rows={3}
          className="input w-full resize-none disabled:bg-gray-50 disabled:text-gray-500"
          placeholder="Anything noteworthy from today's service…"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes for tomorrow</label>
        <textarea
          value={notesForTomorrow}
          onChange={(e) => { setNotesForTomorrow(e.target.value); setDirty(true) }}
          disabled={!canLog}
          rows={3}
          className="input w-full resize-none disabled:bg-gray-50 disabled:text-gray-500"
          placeholder="Handover info, prep reminders, or anything the next team needs to know…"
        />
      </div>

      {canLog && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={!dirty || loading}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-40"
          >
            {loading ? 'Saving…' : 'Save notes'}
          </button>
        </div>
      )}
    </div>
  )
}

export default NotesSection
